import { TokenResponse, useGoogleLogin } from '@react-oauth/google';
import { useState } from 'react';
import { IHandleStartRegistrationProps } from 'types/ui/Registration';
import { authApiClient } from 'utils/apiClient';
import HttpStatusCode from 'utils/HttpStatusCode';

export const useGoogleSignIn = (
  handleSuccessfulLogin: (token: string, name: string) => Promise<void>,
  handleStartRegistration: (
    props: IHandleStartRegistrationProps
  ) => Promise<void>,
  setError: (error?: string) => void
) => {
  const [isGoogleLoading, setIsGoogleLoading] = useState(false);

  const onGoogleLogin = async (
    response: Omit<TokenResponse, 'error' | 'error_description' | 'error_uri'>
  ) => {
    const { status, body } = await authApiClient.public.loginWithGoogle({
      body: {
        accessToken: response.access_token,
      },
    });

    if (status === HttpStatusCode.OK) {
      await handleSuccessfulLogin(body.token, body.welcomeName);
      setIsGoogleLoading(false);
      return;
    } else {
      console.log('google login failed', body.message);
      setError(body.message);
    }
  };

  const onGoogleRegister = async (
    response: Omit<TokenResponse, 'error' | 'error_description' | 'error_uri'>
  ) => {
    setIsGoogleLoading(false);

    const validateRes = await authApiClient.public.validateGoogleAccessToken({
      body: {
        accessToken: response.access_token,
      },
    });

    if (validateRes.status === HttpStatusCode.BAD_REQUEST) {
      setError(validateRes.body.message);
      return;
    } else {
      if (validateRes.status === HttpStatusCode.OK) {
        // found an account so logging in
        handleSuccessfulLogin(
          validateRes.body.token,
          validateRes.body.welcomeName
        );
        return;
      }
    }

    const googleResponse = await fetch(
      'https://www.googleapis.com/oauth2/v3/userinfo',
      {
        headers: {
          Authorization: `Bearer ${response.access_token}`,
        },
      }
    );
    const userDetails = await googleResponse.json();

    handleStartRegistration({
      googleUserId: validateRes.body.googleUserId,
      email: userDetails.email ?? '',
      firstName: userDetails.given_name ?? '',
      lastName: userDetails.family_name ?? '',
    });
  };

  const googleLogin = useGoogleLogin({
    onSuccess: onGoogleLogin,
    onError: (...props) => {
      setIsGoogleLoading(false);
      console.log(props);
    },
    onNonOAuthError: (...props) => {
      setIsGoogleLoading(false);
      console.log(props);
    },
  });

  const googleRegister = useGoogleLogin({
    onSuccess: onGoogleRegister,
    onError: (...props) => {
      setIsGoogleLoading(false);
      console.log(props);
    },
    onNonOAuthError: (...props) => {
      setIsGoogleLoading(false);
      console.log(props);
    },
  });

  const doGoogleSignIn = async () => {
    setIsGoogleLoading(true);
    setError(undefined);
    await googleLogin();
  };

  const doGoogleRegister = async () => {
    setIsGoogleLoading(true);
    setError(undefined);
    await googleRegister();
  };

  return { isGoogleLoading, doGoogleSignIn, doGoogleRegister };
};
